import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Lock, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* iter-80 — RestrictedEmail.
   For high-value ownership/executive contacts (e.g. Panna Utpaul, Pedro
   Pereira, Dwight Tabales) we hide the raw email from non-admin
   viewers. Admins and engineers see the email plainly; everyone else
   gets a blurred placeholder with a `Lock` icon. Clicking the
   placeholder POSTs an access-request to `/api/access-requests`
   (existing endpoint) with a `contact:{slug}` button id so the admin
   sees the disclosure ask in the same inbox they already monitor for
   button-level access requests. */
export default function RestrictedEmail({ email, memberSlug, memberName }) {
  const token = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;
  const role = typeof window !== 'undefined' ? (localStorage.getItem('user_role') || '') : '';
  const isAdmin = ['admin', 'engineer'].includes(role);
  const [busy, setBusy] = useState(false);
  const [requested, setRequested] = useState(false);

  // Admins + engineers see the plain mailto. The Mail icon and styling
  // mirror the rest of the team card so visually nothing changes for
  // them — only the rendering branch differs.
  if (isAdmin) {
    return (
      <a
        href={`mailto:${email}`}
        className="flex items-center gap-1.5 text-[11px] truncate hover:underline"
        style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}
        data-testid={`email-${memberSlug}`}
      >
        <Mail className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#1F4FBE' }} />
        <span className="truncate">{email}</span>
      </a>
    );
  }

  const requestDisclosure = async () => {
    if (busy || requested) return;
    setBusy(true);
    try {
      if (!token) {
        // Anonymous visitors: skip the API call, just hint at the
        // sign-in flow. Logged-in viewers/editors hit the API.
        toast.message('Sign in to request this contact email — an admin will review your request.');
        return;
      }
      await axios.post(
        `${API}/access-requests`,
        {
          button: `contact:${memberSlug}`,
          duration_hours: 24,
          reason: `Reveal email for ${memberName || memberSlug}`,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setRequested(true);
      toast.success(`Request sent — an admin will review your access to ${memberName || 'this contact'} shortly.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send request. Try again later.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={requestDisclosure}
      disabled={busy || requested}
      data-testid={`email-restricted-${memberSlug}`}
      title={requested ? 'Request pending admin review' : 'Email hidden — click to request disclosure'}
      className="flex items-center gap-1.5 text-[11px] truncate text-left disabled:cursor-default group"
      style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}
    >
      {busy ? (
        <Loader2 className="w-3.5 h-3.5 flex-shrink-0 animate-spin" style={{ color: '#1F4FBE' }} />
      ) : (
        <Lock className="w-3.5 h-3.5 flex-shrink-0" style={{ color: requested ? '#15803D' : '#B45309' }} />
      )}
      <span className="truncate flex-1 transition-all" style={{ filter: requested ? 'none' : 'blur(5px)', userSelect: 'none' }}>
        {/* Always render a plausible-length placeholder so the blur has
            something to obscure. Don't render the real address. */}
        ●●●●●●●●●●@●●●●●●●●●.●●●
      </span>
      <span
        className="text-[9px] font-bold uppercase tracking-wider flex-shrink-0 ml-1 px-1.5 py-0.5 rounded transition-colors"
        style={{
          color: requested ? '#15803D' : '#B45309',
          backgroundColor: requested ? '#DCFCE7' : '#FEF3C7',
        }}
      >
        {requested ? 'Requested' : 'Request'}
      </span>
    </button>
  );
}
